import { z } from 'zod';
const MAX_TERM_LENGTH = 512;
const MAX_TERM_IDENTITY_LENGTH = MAX_TERM_LENGTH + 32;
const MAX_NOTE_LENGTH = 4_000;
const MAX_RESOURCE_NAME_LENGTH = 1_024;
const MAX_BATCH_SIZE = 250;
const nonEmptyStringSchema = z.string().trim().min(1);
const boundedNoteSchema = nonEmptyStringSchema.max(MAX_NOTE_LENGTH);
const internalIdSchema = z
    .string()
    .uuid()
    .transform(value => value.toLowerCase());
export const googleAdsEntityIdSchema = z
    .string()
    .trim()
    .regex(/^\d+$/, 'Expected a numeric Google Ads ID')
    .transform(value => value.replace(/^0+(?=\d)/, ''));
export const googleAdsDateStringSchema = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD Google Ads date');
const resourceNameSchema = nonEmptyStringSchema.max(MAX_RESOURCE_NAME_LENGTH);
const positiveVersionSchema = z.number().int().positive();
const nonNegativeIntegerSchema = z.number().int().nonnegative();
const uuidFingerprintPart = '[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';
const googleAdsEntityIdFingerprintPart = '(?:0|[1-9]\\d*)';
const variantFamilyPattern = /^nkv:v1:.+$/;
const scopeFingerprintPattern = new RegExp(`^nks:v1:(?:` +
    `PARTNER_WIDE:partner=${uuidFingerprintPart}|` +
    `BUSINESS:partner=${uuidFingerprintPart}:business=${uuidFingerprintPart}|` +
    `LOCATION:partner=${uuidFingerprintPart}:business=${uuidFingerprintPart}:location=${uuidFingerprintPart}|` +
    `ACCOUNT:partner=${uuidFingerprintPart}:business=${uuidFingerprintPart}:adAccount=${uuidFingerprintPart}:customer=\\d{10}|` +
    `CAMPAIGN:partner=${uuidFingerprintPart}:business=${uuidFingerprintPart}:adAccount=${uuidFingerprintPart}:customer=\\d{10}:campaign=${googleAdsEntityIdFingerprintPart}|` +
    `AD_GROUP:partner=${uuidFingerprintPart}:business=${uuidFingerprintPart}:adAccount=${uuidFingerprintPart}:customer=\\d{10}:campaign=${googleAdsEntityIdFingerprintPart}:adGroup=${googleAdsEntityIdFingerprintPart}` +
    `)$`);
export const googleAdsCustomerIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(14)
    .transform(value => value.replaceAll('-', ''))
    .pipe(z.string().regex(/^\d{10}$/, 'Expected a 10-digit Google Ads customer ID'));
// Keep micros as decimal strings so Google Ads 64-bit values never lose precision in JavaScript.
export const googleAdsMicrosSchema = z.union([
    z.string().trim().regex(/^\d+$/).max(30),
    z.number().int().nonnegative().safe().transform(String),
]);
export const negativeKeywordSuggestionStatusSchema = z.enum([
    'PENDING_REVIEW',
    'EDITED',
    'REJECTED',
    'APPROVED',
    'APPLY_QUEUED',
    'APPLIED',
    'APPLY_FAILED',
    'SUPPRESSED',
]);
export const negativeKeywordDecisionScopeSchema = z.enum([
    'BUSINESS',
    'LOCATION',
    'PARTNER_WIDE',
    'ACCOUNT',
    'CAMPAIGN',
    'AD_GROUP',
]);
export const negativeKeywordMatchTypeSchema = z.enum(['EXACT', 'PHRASE', 'BROAD']);
export const negativeKeywordRunTriggerSchema = z.enum(['scheduled', 'controlled_backfill']);
export const negativeKeywordCadenceStageSchema = z.enum([
    'onboarding_tuning',
    'stabilization',
    'steady_state',
]);
export const negativeKeywordActivityEligibilityStateSchema = z.enum(['eligible', 'skipped', 'unknown']);
export const negativeKeywordSkipReasonSchema = z.enum([
    'NO_RECENT_SEARCH_ACTIVITY',
    'LOW_RECENT_COST',
    'MISSING_GOOGLE_ADS_CONNECTION',
    'NO_SUPPORTED_CAMPAIGNS',
    'UNKNOWN',
]);
export const negativeKeywordSuppressionStatusSchema = z.enum(['ACTIVE', 'CLEARED']);
export const negativeKeywordSuppressionReasonSchema = z.enum([
    'OPERATOR_REJECTED',
    'REJECTED_TWICE',
    'MANUAL_SUPPRESSION',
    'SAFETY_BLOCK',
]);
export const negativeKeywordTargetResourceTypeSchema = z.enum([
    'SHARED_SET',
    'SHARED_CRITERION',
    'CAMPAIGN_SHARED_SET',
    'CUSTOMER_NEGATIVE_CRITERION',
    'CAMPAIGN_CRITERION',
    'AD_GROUP_CRITERION',
]);
export const negativeKeywordDateWindowSchema = z
    .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
})
    .strict()
    .refine(window => window.startDate <= window.endDate, {
    message: 'startDate must be on or before endDate',
    path: ['endDate'],
});
const partnerWideScopeSchema = z
    .object({
    decisionScope: z.literal('PARTNER_WIDE'),
    partnerId: internalIdSchema,
})
    .strict();
const businessScopeSchema = z
    .object({
    decisionScope: z.literal('BUSINESS'),
    partnerId: internalIdSchema,
    businessId: internalIdSchema,
})
    .strict();
const locationScopeSchema = z
    .object({
    decisionScope: z.literal('LOCATION'),
    partnerId: internalIdSchema,
    businessId: internalIdSchema,
    locationId: internalIdSchema,
})
    .strict();
const accountScopeSchema = z
    .object({
    decisionScope: z.literal('ACCOUNT'),
    partnerId: internalIdSchema,
    businessId: internalIdSchema,
    adAccountId: internalIdSchema,
    googleCustomerId: googleAdsCustomerIdSchema,
})
    .strict();
const campaignScopeSchema = z
    .object({
    decisionScope: z.literal('CAMPAIGN'),
    partnerId: internalIdSchema,
    businessId: internalIdSchema,
    adAccountId: internalIdSchema,
    googleCustomerId: googleAdsCustomerIdSchema,
    campaignId: googleAdsEntityIdSchema,
})
    .strict();
const adGroupScopeSchema = z
    .object({
    decisionScope: z.literal('AD_GROUP'),
    partnerId: internalIdSchema,
    businessId: internalIdSchema,
    adAccountId: internalIdSchema,
    googleCustomerId: googleAdsCustomerIdSchema,
    campaignId: googleAdsEntityIdSchema,
    adGroupId: googleAdsEntityIdSchema,
})
    .strict();
export const negativeKeywordScopeSchema = z.discriminatedUnion('decisionScope', [
    partnerWideScopeSchema,
    businessScopeSchema,
    locationScopeSchema,
    accountScopeSchema,
    campaignScopeSchema,
    adGroupScopeSchema,
]);
export const negativeKeywordScopeFingerprintSchema = z
    .string()
    .min(1)
    .max(512)
    .regex(scopeFingerprintPattern, 'Expected a canonical V1 negative-keyword scope fingerprint')
    .brand();
/** Build the only supported V1 suppression/suggestion scope identity. */
export const buildNegativeKeywordScopeFingerprint = (input) => {
    const scope = negativeKeywordScopeSchema.parse(input);
    const parts = [`nks:v1:${scope.decisionScope}`, `partner=${scope.partnerId}`];
    switch (scope.decisionScope) {
        case 'PARTNER_WIDE':
            break;
        case 'BUSINESS':
            parts.push(`business=${scope.businessId}`);
            break;
        case 'LOCATION':
            parts.push(`business=${scope.businessId}`, `location=${scope.locationId}`);
            break;
        case 'ACCOUNT':
            parts.push(`business=${scope.businessId}`, `adAccount=${scope.adAccountId}`, `customer=${scope.googleCustomerId}`);
            break;
        case 'CAMPAIGN':
            parts.push(`business=${scope.businessId}`, `adAccount=${scope.adAccountId}`, `customer=${scope.googleCustomerId}`, `campaign=${scope.campaignId}`);
            break;
        case 'AD_GROUP':
            parts.push(`business=${scope.businessId}`, `adAccount=${scope.adAccountId}`, `customer=${scope.googleCustomerId}`, `campaign=${scope.campaignId}`, `adGroup=${scope.adGroupId}`);
            break;
    }
    return negativeKeywordScopeFingerprintSchema.parse(parts.join(':'));
};
/**
 * Google Ads negative keywords match only the exact word form supplied — a BROAD
 * negative on `denture` does not block `dentures`. This normalizer therefore exists
 * to give dedupe, suppression, and queue grouping a stable key; it never widens
 * coverage. Diacritics are preserved so distinct non-English terms stay distinct.
 */
export const normalizeNegativeKeywordTerm = (rawTerm) => rawTerm
    .normalize('NFKC')
    // Strip only a full match-type wrap with no interior delimiter, so terms like
    // `[a] and [b]` keep their inner brackets/quotes intact.
    .replace(/^\s*\[([^\][]*)\]\s*$/, '$1')
    .replace(/^\s*"([^"]*)"\s*$/, '$1')
    .replace(/(^|\s)\+(?=\S)/g, '$1')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
export const negativeKeywordNormalizedTermSchema = z
    .string()
    .transform(normalizeNegativeKeywordTerm)
    .pipe(z.string().min(1).max(MAX_TERM_LENGTH));
/**
 * Minimal trailing-`s`/`es` rule only. Anything more aggressive risks collapsing
 * genuinely distinct suggestions, which operators would never see in the queue.
 */
const singularizeVariantToken = (token) => {
    const hasSibilantPluralSuffix = /(?:s|x|z|ch|sh)es$/.test(token);
    if (hasSibilantPluralSuffix) {
        return token.slice(0, -2);
    }
    const hasPlainPluralSuffix = token.length > 3 && token.endsWith('s') && !token.endsWith('ss');
    if (hasPlainPluralSuffix) {
        return token.slice(0, -1);
    }
    return token;
};
export const negativeKeywordVariantFamilySchema = z
    .string()
    .max(MAX_TERM_IDENTITY_LENGTH)
    .regex(variantFamilyPattern, 'Expected a canonical V1 negative-keyword variant family')
    .brand();
/**
 * Groups sibling surface forms into one operator decision. This is a grouping key,
 * NOT proof of coverage — the actual variant surface forms are enumerated by the
 * suggestion pipeline and applied individually.
 */
export const buildNegativeKeywordVariantFamily = (rawTerm) => {
    const normalizedTerm = negativeKeywordNormalizedTermSchema.parse(rawTerm);
    const singularizedTokens = normalizedTerm.split(' ').map(singularizeVariantToken).filter(Boolean);
    const uniqueSortedTokens = Array.from(new Set(singularizedTokens)).sort();
    return negativeKeywordVariantFamilySchema.parse(`nkv:v1:${uniqueSortedTokens.join(' ')}`);
};
export const negativeKeywordSourceEvidenceSchema = z
    .object({
    searchTerms: z
        .array(z
        .object({
        query: nonEmptyStringSchema.max(MAX_TERM_LENGTH),
        campaignId: googleAdsEntityIdSchema.optional(),
        campaignName: nonEmptyStringSchema.max(512).optional(),
        adGroupId: googleAdsEntityIdSchema.optional(),
        adGroupName: nonEmptyStringSchema.max(512).optional(),
        impressions: nonNegativeIntegerSchema.optional(),
        clicks: nonNegativeIntegerSchema.optional(),
        costMicros: googleAdsMicrosSchema.optional(),
        conversions: z.number().nonnegative().finite().optional(),
    })
        .strict())
        .max(100)
        .optional(),
    mismatchEvidence: z.array(nonEmptyStringSchema.max(1_000)).max(50).optional(),
    duplicateCoverage: z.array(resourceNameSchema).max(50).optional(),
    protectedTermsMatched: z.array(nonEmptyStringSchema.max(MAX_TERM_LENGTH)).max(50).optional(),
})
    .strict();
export const negativeKeywordSafetyFlagsSchema = z
    .object({
    protectedBrandTerm: z.boolean().optional(),
    protectedLocationTerm: z.boolean().optional(),
    protectedServiceTerm: z.boolean().optional(),
    tenantRunsBrandedSearch: z.boolean().optional(),
    competitorTerm: z.boolean().optional(),
    duplicateCoverage: z.boolean().optional(),
    suppressedTerm: z.boolean().optional(),
    broadMatchRisk: z.boolean().optional(),
})
    .strict();
export const negativeKeywordGoogleAdsResultSchema = z
    .object({
    customerId: googleAdsCustomerIdSchema.optional(),
    resourceType: negativeKeywordTargetResourceTypeSchema.optional(),
    resourceName: resourceNameSchema.optional(),
    requestId: nonEmptyStringSchema.max(512).optional(),
    operation: z.enum(['create', 'remove', 'validate_only']).optional(),
    success: z.boolean().optional(),
    partialFailure: z.boolean().optional(),
    errors: z.array(nonEmptyStringSchema.max(2_000)).max(50).optional(),
})
    .strict();
export const negativeKeywordMutationResultStatusSchema = z.enum(['succeeded', 'failed', 'skipped']);
export const negativeKeywordApplyResultSchema = z
    .object({
    operation: z.literal('apply'),
    suggestionId: internalIdSchema,
    status: negativeKeywordMutationResultStatusSchema,
    resourceType: negativeKeywordTargetResourceTypeSchema.optional(),
    resourceName: resourceNameSchema.optional(),
    failureReason: boundedNoteSchema.optional(),
    errorCode: nonEmptyStringSchema.max(256).optional(),
})
    .strict();
export const negativeKeywordRemovalResultSchema = z
    .object({
    operation: z.literal('remove'),
    suggestionId: internalIdSchema.optional(),
    googleCustomerId: googleAdsCustomerIdSchema,
    resourceType: negativeKeywordTargetResourceTypeSchema,
    resourceName: resourceNameSchema,
    targetListResourceName: resourceNameSchema.optional(),
    status: negativeKeywordMutationResultStatusSchema,
    failureReason: boundedNoteSchema.optional(),
    errorCode: nonEmptyStringSchema.max(256).optional(),
})
    .strict();
export const negativeKeywordInputSummarySchema = z
    .object({
    searchTermCount: nonNegativeIntegerSchema.optional(),
    existingNegativeCount: nonNegativeIntegerSchema.optional(),
    suppressionRuleCount: nonNegativeIntegerSchema.optional(),
    protectedTermCount: nonNegativeIntegerSchema.optional(),
})
    .strict();
export const negativeKeywordQueueCursorPayloadSchema = z
    .object({
    createdAt: z.string().datetime({ offset: true }),
    id: internalIdSchema,
})
    .strict();
// The HTTP API exposes an opaque cursor; API code must decode and validate the payload above.
export const negativeKeywordQueueCursorSchema = z.string().trim().min(1).max(2_048);
export const negativeKeywordQueueFiltersSchema = z
    .object({
    status: negativeKeywordSuggestionStatusSchema.optional(),
    partnerId: internalIdSchema.optional(),
    businessId: internalIdSchema.optional(),
    googleCustomerId: googleAdsCustomerIdSchema.optional(),
    decisionScope: negativeKeywordDecisionScopeSchema.optional(),
    matchType: negativeKeywordMatchTypeSchema.optional(),
    selectedTargetListResourceName: resourceNameSchema.optional(),
    createdAfter: z.string().datetime({ offset: true }).optional(),
    createdBefore: z.string().datetime({ offset: true }).optional(),
    search: nonEmptyStringSchema.max(200).optional(),
    cursor: negativeKeywordQueueCursorSchema.optional(),
    limit: z.coerce.number().int().positive().max(MAX_BATCH_SIZE).optional(),
})
    .strict();
export const negativeKeywordSuggestionEditSchema = z
    .object({
    term: nonEmptyStringSchema.max(MAX_TERM_LENGTH).optional(),
    matchType: negativeKeywordMatchTypeSchema.optional(),
    scope: negativeKeywordScopeSchema.optional(),
    selectedTargetResourceType: negativeKeywordTargetResourceTypeSchema.nullable().optional(),
    selectedTargetListName: nonEmptyStringSchema.max(512).nullable().optional(),
    selectedTargetListResourceName: resourceNameSchema.nullable().optional(),
    reviewNote: boundedNoteSchema.nullable().optional(),
    expectedVersion: positiveVersionSchema,
})
    .strict()
    .refine(edit => Object.keys(edit).some(key => key !== 'expectedVersion'), {
    message: 'At least one editable field is required',
});
export const negativeKeywordSuggestionApprovalSchema = z
    .object({
    expectedVersion: positiveVersionSchema,
    note: boundedNoteSchema.optional(),
})
    .strict();
export const negativeKeywordSuggestionRejectionSchema = z
    .object({
    reason: boundedNoteSchema,
    note: boundedNoteSchema.optional(),
    suppressSimilar: z.boolean().optional(),
    expectedVersion: positiveVersionSchema,
})
    .strict();
export const negativeKeywordApplyItemSchema = z
    .object({
    suggestionId: internalIdSchema,
    expectedVersion: positiveVersionSchema,
})
    .strict();
export const negativeKeywordApplyRequestSchema = z
    .object({
    items: z.array(negativeKeywordApplyItemSchema).min(1).max(MAX_BATCH_SIZE),
    idempotencyKey: nonEmptyStringSchema.max(512),
})
    .strict()
    .superRefine(({ items }, ctx) => {
    const seenSuggestionIds = new Set();
    items.forEach((item, index) => {
        if (seenSuggestionIds.has(item.suggestionId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Duplicate suggestion ID',
                path: ['items', index, 'suggestionId'],
            });
        }
        seenSuggestionIds.add(item.suggestionId);
    });
});
//# sourceMappingURL=google-ads-negative-keywords.js.map